/**
 * @fileoverview Main container component for the Zodiac game board. Manages game modes,
 * board interactions, and provides UI controls for game settings and actions.
 *
 * @llm IMPORTANT: UI components should not contain any game logic.
 * @llm AVOID: Directly modifying game state without going through ZodiacEngineService.
 * @llm AVOID: Duplicating logic that belongs in the game engine.
 * @llm AVOID: mention of specific piece or piece types in this file
 *
 * ARCHITECTURAL GUIDELINES:
 * - Props Interface: No props required - this is a top-level container
 * - State Management: Uses React hooks for local state and GameContext for shared state
 *
 * RESPONSIBILITIES:
 * - Renders the game board and UI controls
 * - Manages game mode switching (Live, Dev, AI)
 * - Currently Handles drag and drop interactions for pieces - but should not
 * - Displays game state information (kings, turn status) - should be refactored out
 * - Provides controls for AI settings and game creation
 * - Manages clipboard functionality for game sharing
 */
import React, { useContext, useEffect, useState, useRef } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import styles from "./BoardContainer.module.css";
import Board from "./Board";
import DevPasscodeModal from "./DevPasscodeModal/DevPasscodeModal";
import { hasDevAccess } from "../utils/devPasscode";
import {
  Position,
  Mode,
  State,
  PieceType,
} from "../services/zodiacEngine/types";
import { Piece } from "../services/zodiacEngine/Piece";
import PieceDrag from "./PieceDrag";
import { GameContext } from "./Providers/GameContext";
import ZodiacEngineService from "./ZodiacEngineService";
import { AuthContext } from "./Providers/AuthContext";
import KingSelectionModal from "./KingSelection/KingSelectionModal";
import TurnPopover from "./TurnPopover/TurnPopover";
import GameEndPopover from "./GameEndPopover/GameEndPopover";
import { allPieceTypes } from "../services/zodiacEngine/const";
import AIThinking from "./MCTSStats/AIThinking";
import AIStats from "./AIStats";
import { AIStats as AIStatsType } from "../services/zodiacEngine/AIController";
import FENHistory from "../services/zodiacEngine/Fen/FENHistory";
import GameInfo from "./GameInfo/GameInfo";
import { HeuristicType } from "../services/zodiacEngine/heuristics/HeuristicInterface";
import AdSenseLayout from "./AdSense/AdSenseLayout";
import OptionsService, {
  ThinkingMode,
} from "../services/options/OptionsService";
import {
  loadTestScenarioFEN,
  TEST_SCENARIO_FEN,
} from "../services/zodiacEngine/Fen/FenLoader";
import {
  SAVED_SCENARIOS,
  getScenarioNames,
  getScenarioByName,
} from "../services/zodiacEngine/heuristics/SavedScenarios";

import { MCTSAnalysisUIData } from "../services/zodiacEngine/MCTS_v2/ui/MCTSAnalysisUIData";
import MCTSAnalytics from "../services/zodiacEngine/MCTS_v2/ui/MCTSAnalytics";

// Phase 5 - SOC Refactoring: Extracted UI components
import { GameModeController } from "./GameModeController/GameModeController";
import { FENLoader } from "./FENLoader/FENLoader";
import { ScenarioSelector } from "./ScenarioSelector/ScenarioSelector";

function BoardContainer() {
  const { navGameId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // Passcode protection state
  const [showPasscodeModal, setShowPasscodeModal] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(false);

  // Check dev access on mount
  useEffect(() => {
    if (hasDevAccess()) {
      setIsAuthorized(true);
    } else {
      setShowPasscodeModal(true);
    }
  }, []);

  useEffect(() => {
    if (navGameId) {
      checkUser();
      ZodiacEngineService.zodiacEngine.joinGame(navGameId, user!.uid);
      setGameId(navGameId);
      setContextGameId(navGameId);
    }
  }, [navGameId]);

  const {
    gameState,
    potentialMoves,
    potentialSideEffects,
    isLightPlayer,
    isThisPlayersTurn,
    updateGameState,
    isAIThinking,
    updateSelectedPiece,
    updatePotentialMoves,
    setIsWatchMode,
    setGameId: setContextGameId,
  } = useContext(GameContext)!;

  const { user } = useContext(AuthContext);


  const [gameId, setGameId] = useState(
    ZodiacEngineService.zodiacEngine.getGameId()
  );
  // Phase 5: joinId state moved to GameModeController
  // Keep copied state for debug section's copyGameId button
  const [copied, setCopied] = useState(false);
  const [isDeepThinking, setIsDeepThinking] = useState(false);
  const [thinkingMode, setThinkingMode] = useState<ThinkingMode>(
    OptionsService.loadThinkingMode()
  );
  const [aiStats, setAiStats] = useState<AIStatsType | null>(null);

  const [testingResults, setTestingResults] = useState<any>(null);
  const [heuristicType, setHeuristicType] = useState<HeuristicType>(
    OptionsService.loadHeuristicType()
  );

  const [mctsAnalysisData, setMctsAnalysisData] = useState<
    MCTSAnalysisUIData | undefined
  >(undefined);
  const [cumulativeMctsLog, setCumulativeMctsLog] = useState<
    string | undefined
  >(undefined);

  const [testAIMode, setTestAIMode] = useState(false);
  // Phase 5: selectedScenario state moved to ScenarioSelector

  // Phase 5: FEN input state (fenInput, fenLoadStatus) moved to FENLoader

  const fenHistoryRef = useRef<any>(null);
  const [moveHistoryResetKey, setMoveHistoryResetKey] = useState(0);
  const [pendingMoveHistoryReset, setPendingMoveHistoryReset] = useState(false);

  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("zodiac-dark-mode") === "true";
    }
    return false;
  });

  const [analyticsKey, setAnalyticsKey] = useState(0);

  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("zodiac-dark-mode", darkMode ? "true" : "false");
    }
  }, [darkMode]);

  const toggleDarkMode = () => setDarkMode((d) => !d);

  useEffect(() => {
    if (
      (gameState.mode === Mode.AI ||
        gameState.mode === Mode.TestAI ||
        gameState.mode === Mode.Watch) &&
      ZodiacEngineService.zodiacEngine.aiController
    ) {
      updateAIStats();
      // Fetch MCTS data when AI mode is active
      const mctsInstance = ZodiacEngineService.zodiacEngine.aiController.mcts;
      if (mctsInstance) {
        const latestData = mctsInstance.getLatestAnalysisDataDirectly();
        const historicalLog = mctsInstance.getHistoricalAnalysesAsString();
        setMctsAnalysisData(latestData);
        setCumulativeMctsLog(historicalLog);
      }

      const intervalId = setInterval(() => {
        updateAIStats();
        // Periodically update MCTS data if needed, or trigger on specific events
        if (
          ZodiacEngineService.zodiacEngine.aiController &&
          ZodiacEngineService.zodiacEngine.aiController.mcts
        ) {
          const latestDataInterval =
            ZodiacEngineService.zodiacEngine.aiController.mcts.getLatestAnalysisDataDirectly();
          const historicalLogInterval =
            ZodiacEngineService.zodiacEngine.aiController.mcts.getHistoricalAnalysesAsString();
          setMctsAnalysisData(latestDataInterval);
          setCumulativeMctsLog(historicalLogInterval);
        }
      }, 1000); // Or a more suitable interval/trigger

      return () => clearInterval(intervalId);
    } else {
      // Clear data if not in AI mode or AI controller is not available
      setMctsAnalysisData(undefined);
      setCumulativeMctsLog(undefined);
    }
  }, [gameState.mode, gameState.turnCount, isAIThinking]); // Added gameState.turnCount and isAIThinking as dependencies

  const updateAIStats = () => {
    if (ZodiacEngineService.zodiacEngine.aiController) {
      const stats = ZodiacEngineService.zodiacEngine.getAIStats();
      if (stats) {
        setAiStats(stats);
      }
    }
  };

  // Phase 5: handleJoinIdChange moved to GameModeController

  function checkUser() {
    if (!user) {
      throw new Error("User has no id for some strange reason");
    }
  }

  async function liveMode() {
    checkUser();
    var gameId = await ZodiacEngineService.zodiacEngine.switchToLiveMove(
      user!.uid
    );
    setGameId(gameId);
    setContextGameId(gameId);
    window.history.replaceState(null, "", `/${gameId}`);
  }

  // Phase 5: joinGame logic moved to GameModeController's onJoinGame callback

  function devMode() {
    // Disable watch mode when switching to dev mode
    setIsWatchMode(false);

    // Switch to dev mode using the ZodiacEngine service
    ZodiacEngineService.zodiacEngine.switchToDevMove();

    // Deselect any selected piece and remove all highlights
    updateSelectedPiece(undefined);
    updatePotentialMoves([]);
  }

  async function aiMode() {
    checkUser();

    // Disable watch mode when switching to regular AI mode
    setIsWatchMode(false);

    // Make sure any previous game state is fully reset
    // Use a different approach than directly accessing private properties
    ZodiacEngineService.zodiacEngine.switchToDevMove(); // First switch to dev mode

    const eligibleKings = allPieceTypes.filter(
      (piece) => piece !== PieceType.Virus
      // Octopus and Penguin are now allowed as kings
    );

    const randomLightKingIndex = Math.floor(
      Math.random() * eligibleKings.length
    );
    const lightKing = eligibleKings[randomLightKingIndex];

    const randomDarkKingIndex = Math.floor(
      Math.random() * eligibleKings.length
    );
    const darkKing = eligibleKings[randomDarkKingIndex];

    // Force reset AI controller to ensure fresh state
    if (ZodiacEngineService.zodiacEngine.aiController) {
      ZodiacEngineService.zodiacEngine.aiController = null;
    }

    ZodiacEngineService.zodiacEngine.switchToAIMode(
      user!.uid,
      lightKing,
      darkKing
    );

    // Load saved options
    const savedHeuristic = OptionsService.loadHeuristicType();
    const savedThinkingMode = OptionsService.loadThinkingMode();

    // Apply saved options
    setHeuristicType(savedHeuristic);
    setThinkingMode(savedThinkingMode);

    // Set the selected heuristic
    ZodiacEngineService.zodiacEngine.setAIHeuristic(savedHeuristic);

    const newGameState = ZodiacEngineService.zodiacEngine.getGameState();
    const newGameId = ZodiacEngineService.zodiacEngine.getGameId();
    setGameId(newGameId);
    setContextGameId(newGameId ?? null);
    updateGameState(newGameState);

    updateAIThinkingIterations();
    setAnalyticsKey((prev) => prev + 1); // Reset analytics
  }

  async function testAIModeHandler() {
    setTestAIMode(true);
    // Disable watch mode when switching to test AI mode
    setIsWatchMode(false);

    // Phase 5 - Simplified: User selects scenario manually via ScenarioSelector
    // Load default test scenario
    let scenarioToLoad = TEST_SCENARIO_FEN;

    loadTestScenarioFEN(ZodiacEngineService.zodiacEngine, scenarioToLoad);
    // Immediately fetch the updated state from the engine (which should have mode: TestAI)
    const newGameState = ZodiacEngineService.zodiacEngine.getGameState();
 // Debug log
    updateGameState(newGameState);
    const newGameId = ZodiacEngineService.zodiacEngine.getGameId();
    setGameId(newGameId);
    setContextGameId(newGameId ?? null);
    updateAIThinkingIterations();
    setPendingMoveHistoryReset(true); // Set flag to trigger reset after state update
    setAnalyticsKey((prev) => prev + 1); // Reset analytics
  }

  async function watchModeHandler() {
    checkUser();

    // Make sure any previous game state is fully reset
    ZodiacEngineService.zodiacEngine.switchToDevMove(); // First switch to dev mode

    const eligibleKings = allPieceTypes.filter(
      (piece) => piece !== PieceType.Virus
      // Octopus and Penguin are now allowed as kings
    );

    const randomLightKingIndex = Math.floor(
      Math.random() * eligibleKings.length
    );
    const lightKing = eligibleKings[randomLightKingIndex];

    const randomDarkKingIndex = Math.floor(
      Math.random() * eligibleKings.length
    );
    const darkKing = eligibleKings[randomDarkKingIndex];

    // Force reset AI controller to ensure fresh state
    if (ZodiacEngineService.zodiacEngine.aiController) {
      ZodiacEngineService.zodiacEngine.aiController = null;
    }

    ZodiacEngineService.zodiacEngine.switchToWatchMode(lightKing, darkKing);

    // Load saved options
    const savedHeuristic = OptionsService.loadHeuristicType();
    const savedThinkingMode = OptionsService.loadThinkingMode();

    // Apply saved options
    setHeuristicType(savedHeuristic);
    setThinkingMode(savedThinkingMode);

    // Set the selected heuristic
    ZodiacEngineService.zodiacEngine.setAIHeuristic(savedHeuristic);

    const newGameState = ZodiacEngineService.zodiacEngine.getGameState();
    const newGameId = ZodiacEngineService.zodiacEngine.getGameId();
    setGameId(newGameId);
    setContextGameId(newGameId ?? null);
    updateGameState(newGameState);

    updateAIThinkingIterations();
    setAnalyticsKey((prev) => prev + 1); // Reset analytics
  }

  // Robust move history reset after scenario is loaded in context
  useEffect(() => {
    if (pendingMoveHistoryReset && gameState.mode === Mode.TestAI) {
      setMoveHistoryResetKey((k) => k + 1);
      setPendingMoveHistoryReset(false);
    }
  }, [pendingMoveHistoryReset, gameState.mode]);

  function handleDrop(
    event: React.DragEvent<HTMLDivElement>,
    position: Position
  ) {
    event.preventDefault();
    const pieceData = event.dataTransfer.getData("piece");

    if (!pieceData) return;
    const { type, isLight } = JSON.parse(pieceData);

    if (gameState.mode === Mode.Dev || gameState.mode === Mode.Sandbox) {
      ZodiacEngineService.zodiacEngine.placePiece(
        new Piece(type as PieceType, isLight),
        position
      );
      return;
    }

    var isDroppedInStartingRow =
      (isLightPlayer() && position.row === 0) ||
      (!isLightPlayer() && position.row === 7);

    if (
      isThisPlayersTurn() &&
      isDroppedInStartingRow &&
      gameState.mode === Mode.Live &&
      gameState.state === State.PieceSelection
    ) {
      ZodiacEngineService.zodiacEngine.setStartingPiece(
        type as PieceType,
        isLightPlayer(),
        position.col
      );
    }
  }

  function handleDragOver(event: React.DragEvent<HTMLDivElement>) {
    // Block all user interactions when in NN test mode
    event.preventDefault();
  }

  const copyGameIdToClipboard = async () => {
    if (!gameId) return;
    try {
      await navigator.clipboard.writeText(gameId);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      alert("Failed to copy!");
    }
  };

  // Phase 5 - Refactored: GameModeController now handles the copy state
  const copyLinkToClipboard = async () => {
    if (!gameId) return;
    var path = window.location.origin + location.pathname + gameId;
    try {
      await navigator.clipboard.writeText(path);
    } catch (err) {
      alert("Failed to copy!");
    }
  };

  function isPieceSelectionOpen(): boolean {
    return gameState.state === State.KingSelection;
  }

  const toggleThinkingMode = () => {
    // Get the current thinking mode
    const currentMode = thinkingMode;

    // Cycle through the modes: fast -> deep -> ultra -> fast
    let newMode: ThinkingMode;
    if (currentMode === "Fast") {
      newMode = "Normal";
    } else if (currentMode === "Normal") {
      newMode = "Deep";
    } else {
      newMode = "Fast";
    }

    // Set the new thinking mode
    setThinkingMode(newMode);

    // Save thinking mode to persistent storage
    OptionsService.saveThinkingMode(newMode);

    // Update AI thinking iterations based on mode
    if (ZodiacEngineService.zodiacEngine.aiController) {
      if (newMode === "Fast") {
        ZodiacEngineService.zodiacEngine.aiController.setSearchIterations(500);
      } else if (newMode === "Normal") {
        ZodiacEngineService.zodiacEngine.aiController.setSearchIterations(3000);
      } else if (newMode === "Deep") {
        ZodiacEngineService.zodiacEngine.aiController.setSearchIterations(
          10000
        );
      }
    }

  };

  const updateAIThinkingIterations = () => {
    // Use the current thinking mode from state
    const currentThinkingMode = thinkingMode;

    if (currentThinkingMode === "Deep") {
      ZodiacEngineService.zodiacEngine.setAISearchIterations(10000);
      ZodiacEngineService.zodiacEngine.setAIExplorationParameter(2.0);
    } else if (currentThinkingMode === "Normal") {
      ZodiacEngineService.zodiacEngine.setAISearchIterations(3000);
      ZodiacEngineService.zodiacEngine.setAIExplorationParameter(1.414);
    } else {
      ZodiacEngineService.zodiacEngine.setAISearchIterations(500);
      ZodiacEngineService.zodiacEngine.setAIExplorationParameter(1.0);
    }
  };

  const handleHeuristicChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const newHeuristic = event.target.value as HeuristicType;
    setHeuristicType(newHeuristic);
    ZodiacEngineService.zodiacEngine.setAIHeuristic(newHeuristic);

    // Save heuristic type to persistent storage
    OptionsService.saveHeuristicType(newHeuristic);

  };

  // Phase 5 - Refactored: Now receives scenarioName from ScenarioSelector
  const handleScenarioChange = (scenarioName: string) => {
    if (scenarioName) {
      const scenario = getScenarioByName(scenarioName);
      if (scenario) {
        loadTestScenarioFEN(ZodiacEngineService.zodiacEngine, scenario.fen);
        // Update the game state after loading the scenario
        const newGameState = ZodiacEngineService.zodiacEngine.getGameState();
        updateGameState(newGameState);
        const newGameId = ZodiacEngineService.zodiacEngine.getGameId();
        setGameId(newGameId);
        setContextGameId(newGameId ?? null);
        updateAIThinkingIterations();
        setPendingMoveHistoryReset(true);
      }
    }
  };

  // Phase 5: handleFenLoad and handleFenInputChange moved to FENLoader component

  // Update AI thinking iterations whenever the thinking mode changes
  useEffect(() => {
    if (
      (gameState.mode === Mode.AI || gameState.mode === Mode.Watch) &&
      ZodiacEngineService.zodiacEngine.aiController
    ) {
      updateAIThinkingIterations();
    }
  }, [thinkingMode]);

  function getKingDisplay(isLight: boolean): string {
    // In Test AI mode or Watch mode, always show the king for both sides
    if (gameState.mode === Mode.TestAI || gameState.mode === Mode.Watch) {
      return getKingName(isLight);
    }
    if (gameState.mode === Mode.Dev) return "Dev Mode, no kings";
    if (gameState.mode === Mode.Sandbox) return "Sandbox Mode, no kings";
    return getKingName(isLight);
  }

  function getKingName(isLight: boolean): string {
    // In Test AI mode or Watch mode, always show the king for both sides
    if (gameState.mode === Mode.TestAI || gameState.mode === Mode.Watch) {
      const kingName = isLight ? gameState.lightKing : gameState.darkKing;
      return kingName ? kingName : "Not Set";
    }
    const kingName = isLight ? gameState.lightKing : gameState.darkKing;
    const isThisPlayersKing = isLight === isLightPlayer();
    if (kingName) {
      // post game show kings to both players
      if (gameState.state === State.PostGame) {
        return kingName;
      }
      if (isThisPlayersKing) {
        return kingName;
      } else {
        return "King hidden";
      }
    } else return "Not Set";
  }

  const handlePasscodeSuccess = () => {
    setIsAuthorized(true);
    setShowPasscodeModal(false);
  };

  const handlePasscodeCancel = () => {
    setShowPasscodeModal(false);
    navigate('/main');
  };

  // Don't render the main content until authorized
  if (!isAuthorized) {
    return (
      <DevPasscodeModal
        isOpen={showPasscodeModal}
        onSuccess={handlePasscodeSuccess}
        onCancel={handlePasscodeCancel}
      />
    );
  }

  return (
    <AdSenseLayout>
      <div className={styles.container + (darkMode ? " " + styles.dark : "")}>
        <button
          className={styles.themeToggle}
          onClick={toggleDarkMode}
          aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
          title={darkMode ? "Switch to light mode" : "Switch to dark mode"}
        >
          {darkMode ? "☀️" : "🌙"}
        </button>
        <TurnPopover
        isThisPlayersTurn={gameState.isLightTurn === isLightPlayer()}
        state={gameState.state}
        mode={gameState.mode}
      />
      <GameEndPopover
        isGameOver={
          gameState.state === State.PostGame || gameState.state === State.Draw
        }
        hasWon={
          gameState.state !== State.Draw &&
          gameState.lightWon !== undefined &&
          gameState.lightWon === isLightPlayer()
        }
        isDraw={gameState.state === State.Draw}
        mode={gameState.mode}
      />
      <KingSelectionModal
        isOpen={isPieceSelectionOpen() && gameState.mode !== Mode.AI}
        isLight={isLightPlayer()}
      />
      <AIThinking
        isVisible={isAIThinking}
        mode={gameState.mode}
        state={gameState.state}
        isDeepThinking={isDeepThinking}
      />
      <div className={styles.gameControls}>
        {/* Phase 5 - SOC Refactoring: Use GameModeController component */}
        <GameModeController
          currentMode={gameState.mode}
          gameId={gameId}
          darkMode={darkMode}
          onCreateGame={liveMode}
          onJoinGame={(id) => {
            checkUser();
            // Empty the board before joining a new game
            ZodiacEngineService.zodiacEngine.getGameState().board.forEach((row) => {
              row.forEach((square) => {
                square.Pieces = [];
              });
            });
            ZodiacEngineService.zodiacEngine.joinGame(id, user!.uid);
          }}
          onDevMode={devMode}
          onAIMode={aiMode}
          onTestAIMode={testAIModeHandler}
          onWatchMode={watchModeHandler}
          onCopyLink={copyLinkToClipboard}
        />

        {/* Phase 5 - SOC Refactoring: Use FENLoader component */}
        <FENLoader
          darkMode={darkMode}
          onLoadFEN={(fenString) => {
            const result = ZodiacEngineService.zodiacEngine.loadFromFEN(fenString);

            if (result.success) {
              // Update the game state after loading the FEN
              const newGameState = ZodiacEngineService.zodiacEngine.getGameState();
              updateGameState(newGameState);
              const newGameId = ZodiacEngineService.zodiacEngine.getGameId();
              setGameId(newGameId);
              setContextGameId(newGameId ?? null);

              // Trigger move history reset
              setMoveHistoryResetKey((prev) => prev + 1);
              setPendingMoveHistoryReset(true);

              setTimeout(() => {
                setPendingMoveHistoryReset(false);
              }, 2000);
            }

            return result;
          }}
        />

        {/* Phase 5 - SOC Refactoring: Use ScenarioSelector component */}
        {gameState.mode === Mode.TestAI && (
          <ScenarioSelector
            darkMode={darkMode}
            onScenarioChange={handleScenarioChange}
          />
        )}
      </div>
      <div className={styles.gameContent}>
        <div className={styles.boardWrapper}>
          {(gameState.state === State.PieceSelection ||
            gameState.mode === Mode.Dev ||
            gameState.mode === Mode.Sandbox) && (
            <div className={styles.pieceSelection}>
              <PieceDrag
                isDevMode={gameState.mode === Mode.Dev || gameState.mode === Mode.Sandbox}
                isLight={isLightPlayer()}
              />
            </div>
          )}
          <Board
            gameState={gameState}
            uiState={{
              potentialMoves: potentialMoves,
              potentialSideEffects: potentialSideEffects,
            }}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
          />
        </div>

        <div className={styles.historyContainer}>
          <MCTSAnalytics
            key={analyticsKey}
            mctsAnalysisData={mctsAnalysisData}
            cumulativeLog={cumulativeMctsLog}
          />
          <FENHistory
            ref={fenHistoryRef}
            moveHistoryResetKey={moveHistoryResetKey}
          />
          <GameInfo />
        </div>
      </div>
      <div className={styles.debug}>
        Game Id: {gameId ? gameId!.slice(0, 8) + "..." : "No Game Id"}
        <br />
        <button onClick={copyGameIdToClipboard}>
          {copied ? "Copied!" : "Copy Game Id"}
        </button>
        <br />
        {testingResults && (
          <>
            Last Test Result: {testingResults.success ? "Success" : "Failed"}{" "}
            <br />
            {testingResults.success ? (
              <>
                Move: {testingResults.moveDetails.from} →{" "}
                {testingResults.moveDetails.to} <br />
                Piece: {testingResults.moveDetails.piece} <br />
                Capture: {testingResults.moveDetails.isCapture
                  ? "Yes"
                  : "No"}{" "}
                <br />
              </>
            ) : (
              <>
                Error: {testingResults.error} <br />
              </>
            )}
          </>
        )}
        State: {gameState.state} <br />
        Whose Turn: {gameState.isLightTurn ? "Light" : "Dark"} <br />
        This player: {isLightPlayer() ? "Light" : "Dark"} <br />
        Turn count: {gameState.turnCount} <br />
        AI Thinking: {isAIThinking ? "Yes" : "No"} <br />
        {gameState.mode === Mode.AI && (
          <>
            AI Heuristic: {heuristicType} <br />
          </>
        )}
        <br />
        {(gameState.mode === Mode.AI ||
          gameState.mode === Mode.TestAI ||
          gameState.mode === Mode.Watch) && <AIStats aiStats={aiStats} />}
        <br />
        Light King: {getKingDisplay(true)}
        <br />
        Dark King: {getKingDisplay(false)}
      </div>
      </div>
    </AdSenseLayout>
  );
}

export default BoardContainer;
